import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { ContactPageOutlined } from '@mui/icons-material';
import { Checkbox, Grid, Link } from '@mui/material';
import { Widget, WidgetContent, WidgetHeader, WidgetIcon, useScrollIntoView } from '@procureai/ui-mui';
import { isNil } from 'ramda';
import { useTranslation } from 'react-i18next';

import { NULLISH_VALUE } from '~/common/constants';
import { I18nNamespace } from '~/common/types';
import { DimSupplierField } from '~/common/types/cubes/DimSupplier';
import { DimTaxIdField } from '~/common/types/cubes/DimTaxId';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { LabeledDataRow } from '~/components/LabeledDataRow/LabeledDataRow';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { toBoolean } from '~/util/cube/toBoolean';

export const SupplierDetailsWidget = () => {
  const { entityId } = useModuleContext();
  const { t: tCommon } = useTranslation(I18nNamespace.common);
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'SupplierDetailsWidget' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'supplier-details' });

  const filters: Filter[] = [
    {
      member: DimSupplierField.ID,
      operator: 'equals',
      values: [entityId],
    },
  ];

  const { isLoading, resultSet } = useCubeQuery({
    filters,
    dimensions: [
      DimSupplierField.DUNS,
      DimSupplierField.IS_POSTING_BLOCKED,
      DimSupplierField.IS_PURCHASING_BLOCKED,
      DimSupplierField.WEBSITE,
      DimSupplierField.CREATED_AT,
    ],
  });
  const supplier = resultSet?.rawData()?.[0];

  const website = supplier?.[DimSupplierField.WEBSITE] as string | undefined;
  const duns = supplier?.[DimSupplierField.DUNS] as string | undefined;
  const isPostingBlocked = toBoolean(supplier?.[DimSupplierField.IS_POSTING_BLOCKED]);
  const isPurchasingBlocked = toBoolean(supplier?.[DimSupplierField.IS_PURCHASING_BLOCKED]);

  const { resultSet: taxIdResultSet } = useCubeQuery({
    filters: [
      {
        member: DimTaxIdField.SUPPLIER_ID,
        operator: 'equals',
        values: [entityId],
      },
    ],
    dimensions: [DimTaxIdField.ID, DimTaxIdField.VALUE, DimTaxIdField.VARIANT],
  });
  return (
    <Widget ref={widgetRef} active={active} data-intercom-target="SupplierSummaryWidget">
      <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<ContactPageOutlined fontSize="small" />} />} />
      <WidgetContent>
        {!isNil(supplier) ? (
          <Grid
            container
            direction="column"
            spacing={4}
            sx={{
              justifyContent: 'flex-start',
            }}
          >
            <Grid container size={{ xs: 12 }}>
              <LabeledDataRow
                labelColumnSize={6}
                label={t('website')}
                body={
                  website ? (
                    <Link color="inherit" href={website} target="_blank">
                      {website}
                    </Link>
                  ) : (
                    NULLISH_VALUE
                  )
                }
              />
            </Grid>
            <Grid container size={{ xs: 12 }}>
              <LabeledDataRow labelColumnSize={6} label={t('duns')} body={duns ?? NULLISH_VALUE} />
            </Grid>
            <Grid container size={{ xs: 12 }} dir="row">
              <LabeledDataRow
                labelColumnSize={6}
                label={t('isPostingBlocked')}
                bodyProps={{ component: 'div' }}
                body={<Checkbox sx={{ p: 0, m: 0 }} disabled checked={isPostingBlocked} />}
              />
            </Grid>
            <Grid container size={{ xs: 12 }} dir="row">
              <LabeledDataRow
                labelColumnSize={6}
                label={t('isPurchasingBlocked')}
                bodyProps={{ component: 'div' }}
                body={<Checkbox sx={{ p: 0, m: 0 }} disabled checked={isPurchasingBlocked} />}
              />
            </Grid>
            {taxIdResultSet?.rawData()?.map((taxId) => {
              const id = taxId[DimTaxIdField.ID] as string;
              const taxIdValue = taxId[DimTaxIdField.VALUE] as string;
              const taxIdVariant = taxId[DimTaxIdField.VARIANT] as string;
              return (
                <Grid container size={{ xs: 12 }} key={id}>
                  <LabeledDataRow
                    labelColumnSize={6}
                    label={tCommon(`TaxIdVariant.${taxIdVariant}`)}
                    body={taxIdValue}
                  />
                </Grid>
              );
            })}
          </Grid>
        ) : isLoading ? (
          <CircularProgressCentered sx={{ position: 'relative' }} />
        ) : (
          <PlaceholderWidget />
        )}
      </WidgetContent>
    </Widget>
  );
};
